/**
 * Generate a stable UUID-like identifier across secure and non-secure contexts.
 *
 * `crypto.randomUUID()` is unavailable on non-secure HTTP origins (like
 * http://192.168.x.x). This helper falls back to `getRandomValues` and then
 * to a Math.random-based generator so auth/device flows keep working on LAN.
 */
export function createPortableUuid(): string {
  const cryptoApi = (globalThis as any)?.crypto;

  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }

  if (cryptoApi?.getRandomValues) {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    // RFC4122 v4 variant bits.
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b: number) => b.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).slice(1);
  return `${s4()}${s4()}-${s4()}-4${s4().slice(1)}-${((8 + Math.random() * 4) | 0).toString(16)}${s4().slice(1)}-${s4()}${s4()}${s4()}`;
}

function fnv1a32(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = (hash * 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

/**
 * Build a deterministic browser device ID that is stable across origins
 * (localhost vs LAN IP) on the same machine/browser profile.
 */
export function createStableWebDeviceId(): string {
  try {
    const n = globalThis.navigator;
    const s = globalThis.screen;
    const parts = [
      n?.userAgent || "",
      n?.platform || "",
      n?.vendor || "",
      String((n as any)?.hardwareConcurrency || ""),
      String((n as any)?.deviceMemory || ""),
      String(s?.width || ""),
      String(s?.height || ""),
      Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    ];
    return `webfp-${fnv1a32(parts.join("|"))}`;
  } catch {
    return `webfp-${createPortableUuid().replace(/-/g, "")}`;
  }
}
